import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { RadioEvent, EventType, EventStatus, EventModality, EventCategory } from '@/mocks/adminEvents';
import {
  getStationEvents, eventTypeLabels, eventTypeIcons, eventTypeColors,
  eventStatusLabels, eventStatusColors,
  eventModalityLabels, eventModalityIcons, eventModalityColors,
} from '@/mocks/adminEvents';
import EventCard from './components/EventCard';
import EventFilters from './components/EventFilters';
import EventFormModal from './components/EventFormModal';
import EventDetailModal from './components/EventDetailModal';
import NotificationFlowModal from '@/pages/admin/notifications/components/NotificationFlowModal';
import type { NotificationFlowContext } from '@/pages/admin/notifications/components/NotificationFlowModal';

type LoadState = 'loading' | 'ready' | 'error' | 'empty';

function formatKpi(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return k >= 10 ? Math.round(k).toLocaleString() + 'K' : k.toFixed(1) + 'K';
  }
  return n.toLocaleString();
}

function eventDateLabel(event: RadioEvent): string {
  const d = new Date(event.date + 'T' + event.time);
  return d.toLocaleDateString('es-MX', { weekday: 'long', month: 'long', day: 'numeric' }) + ' · ' + event.time + ' hs';
}

export default function AdminEventsPage() {
  const [dataState, setDataState] = useState<LoadState>('loading');
  const [events, setEvents] = useState<RadioEvent[]>([]);
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState<EventType | 'todos'>('todos');
  const [selectedStatus, setSelectedStatus] = useState<EventStatus | 'todos'>('todos');
  const [selectedModality, setSelectedModality] = useState<EventModality | 'todos'>('todos');
  const [selectedCategory, setSelectedCategory] = useState<EventCategory | 'todos'>('todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<RadioEvent | null>(null);
  const [detailModal, setDetailModal] = useState<{ open: boolean; event: RadioEvent | null }>({
    open: false,
    event: null,
  });
  const [flowModal, setFlowModal] = useState<{ open: boolean; context: NotificationFlowContext }>({
    open: false,
    context: { type: 'evento', relatedModule: 'events' },
  });
  const mountedRef = useRef(false);

  const loadData = () => {
    setDataState('loading');
    try {
      const data = getStationEvents(1);
      if (!data || data.length === 0) {
        setDataState('empty');
        setEvents([]);
      } else {
        setEvents(data);
        setDataState('ready');
      }
    } catch {
      setDataState('error');
    }
  };

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      const timer = setTimeout(() => loadData(), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  const retry = () => loadData();

  /* Aggregate KPIs */
  const kpiTotals = useMemo(() => {
    const publicEvents = events.filter(e => e.status === 'publicado' || e.status === 'finalizado');
    return {
      participants: events.reduce((s, e) => s + e.participants, 0),
      interested: events.reduce((s, e) => s + e.interested, 0),
      shares: events.reduce((s, e) => s + e.shares, 0),
      followers: events.reduce((s, e) => s + e.followers, 0),
    };
  }, [events]);

  /* Counters */
  const counts = useMemo(() => {
    const typeCounts: Record<string, number> = {};
    const statusCounts: Record<string, number> = {};
    const modalityCounts: Record<string, number> = {};
    const categoryCounts: Record<string, number> = {};
    const types: EventType[] = ['concierto', 'meet_greet', 'transmision_especial', 'firma_autografos', 'sesion_acustica', 'lanzamiento'];
    const statuses: EventStatus[] = ['borrador', 'publicado', 'finalizado', 'cancelado'];
    const modalities: EventModality[] = ['presencial', 'online', 'hibrido'];
    const categories: EventCategory[] = ['musica', 'deportes', 'comunidad', 'cultural', 'religioso', 'familiar', 'educativo'];

    types.forEach((t) => { typeCounts[t] = events.filter((e) => e.type === t).length; });
    statuses.forEach((s) => { statusCounts[s] = events.filter((e) => e.status === s).length; });
    modalities.forEach((m) => { modalityCounts[m] = events.filter((e) => e.modality === m).length; });
    categories.forEach((c) => { categoryCounts[c] = events.filter((e) => e.category === c).length; });

    return { todos: events.length, types: typeCounts, statuses: statusCounts, modalities: modalityCounts, categories: categoryCounts };
  }, [events]);

  /* Modality section counts */
  const presencialEvents = events.filter(e => e.modality === 'presencial');
  const onlineEvents = events.filter(e => e.modality === 'online');
  const hibridoEvents = events.filter(e => e.modality === 'hibrido');

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      const matchesSearch = e.title.toLowerCase().includes(search.toLowerCase()) || e.description.toLowerCase().includes(search.toLowerCase()) || e.location.toLowerCase().includes(search.toLowerCase());
      const matchesType = selectedType === 'todos' || e.type === selectedType;
      const matchesStatus = selectedStatus === 'todos' || e.status === selectedStatus;
      const matchesModality = selectedModality === 'todos' || e.modality === selectedModality;
      const matchesCategory = selectedCategory === 'todos' || e.category === selectedCategory;
      return matchesSearch && matchesType && matchesStatus && matchesModality && matchesCategory;
    });
  }, [events, search, selectedType, selectedStatus, selectedModality, selectedCategory]);

  const publishedCount = events.filter((e) => e.status === 'publicado').length;

  /* Handlers */
  const handleSave = (data: Partial<RadioEvent>) => {
    if (data.id && events.find((e) => e.id === data.id)) {
      setEvents((prev) => prev.map((e) => (e.id === data.id ? { ...e, ...data } as RadioEvent : e)));
    } else {
      setEvents((prev) => [
        { ...data, id: Date.now(), stationId: 1, participants: 0, interested: 0, shares: 0, followers: 0 } as RadioEvent,
        ...prev,
      ]);
    }
    if (dataState === 'empty') setDataState('ready');
  };

  const handleDelete = async (event: RadioEvent) => {
    const result = await SwalAlert.confirm(
      '¿Eliminar evento?',
      `Se eliminará permanentemente "${event.title}". Esta acción no se puede deshacer.`,
    );
    if (result.isConfirmed) {
      setEvents((prev) => {
        const next = prev.filter((e) => e.id !== event.id);
        if (next.length === 0) setDataState('empty');
        return next;
      });
      await SwalAlert.toast('Evento eliminado.', 'success');
    }
  };

  const handleShare = async (event: RadioEvent) => {
    const shareUrl = `https://radioelnorte.com/events/${event.id}`;
    const result = await SwalAlert.fire({
      title: 'Compartir evento',
      html: `
        <div class="text-left space-y-3">
          <p class="text-foreground-600 text-xs">Copiá el enlace o compartí directamente:</p>
          <div class="flex items-center gap-2 p-2.5 rounded-lg bg-background-100 border border-background-200">
            <code class="text-foreground-700 text-[11px] flex-1 break-all select-all">${shareUrl}</code>
          </div>
          <div class="grid grid-cols-4 gap-2">
            <button onclick="navigator.clipboard.writeText('${shareUrl}'); Swal.close();" class="flex flex-col items-center gap-1 p-2.5 rounded-xl bg-background-100 hover:bg-background-200 cursor-pointer transition-colors border border-background-200">
              <i class="ri-link text-sky-500 text-lg"></i>
              <span class="text-[9px] text-foreground-600 font-semibold">Copiar</span>
            </button>
            <a href="https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}" target="_blank" class="flex flex-col items-center gap-1 p-2.5 rounded-xl bg-background-100 hover:bg-background-200 cursor-pointer transition-colors border border-background-200 no-underline">
              <i class="ri-facebook-fill text-sky-600 text-lg"></i>
              <span class="text-[9px] text-foreground-600 font-semibold">Facebook</span>
            </a>
            <a href="https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(event.title)}" target="_blank" class="flex flex-col items-center gap-1 p-2.5 rounded-xl bg-background-100 hover:bg-background-200 cursor-pointer transition-colors border border-background-200 no-underline">
              <i class="ri-twitter-x-fill text-foreground-700 text-lg"></i>
              <span class="text-[9px] text-foreground-600 font-semibold">X</span>
            </a>
            <a href="https://wa.me/?text=${encodeURIComponent(event.title + ' ' + shareUrl)}" target="_blank" class="flex flex-col items-center gap-1 p-2.5 rounded-xl bg-background-100 hover:bg-background-200 cursor-pointer transition-colors border border-background-200 no-underline">
              <i class="ri-whatsapp-fill text-emerald-600 text-lg"></i>
              <span class="text-[9px] text-foreground-600 font-semibold">WhatsApp</span>
            </a>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: 'Cerrar',
      customClass: { popup: 'rounded-2xl border border-background-200/70' },
    });
    if (result.isDismissed) {
      setEvents((prev) => prev.map((e) => (e.id === event.id ? { ...e, shares: e.shares + 1 } : e)));
    }
  };

  const handlePublish = async (event: RadioEvent) => {
    const result = await SwalAlert.confirm(
      '¿Publicar evento?',
      `"${event.title}" será visible para todos los oyentes en la página pública de la radio.`,
    );
    if (result.isConfirmed) {
      setEvents((prev) => prev.map((e) => (e.id === event.id ? { ...e, status: 'publicado' as EventStatus } : e)));
      await SwalAlert.success('¡Evento publicado!', `"${event.title}" ya está disponible para tu audiencia.`);

      // Offer to notify
      SwalAlert.confirm(
        '¿Notificar a tus oyentes?',
        `¿Querés enviar una notificación push a tu audiencia sobre "${event.title}"?`,
      ).then((notifyResult) => {
        if (notifyResult.isConfirmed) {
          setFlowModal({
            open: true,
            context: {
              type: 'evento',
              relatedModule: 'events',
              relatedItemId: event.id,
              relatedItemName: event.title,
              prefilledTitle: `¡${event.title}!`,
              prefilledMessage: `Acabamos de publicar "${event.title}". ${eventDateLabel(event)}. ¡No te lo pierdas!`,
            },
          });
        }
      });
    }
  };

  const handleFinalize = async (event: RadioEvent) => {
    const result = await SwalAlert.confirm(
      '¿Finalizar evento?',
      `"${event.title}" se marcará como finalizado. Los oyentes ya no podrán confirmar asistencia.`,
    );
    if (result.isConfirmed) {
      setEvents((prev) => prev.map((e) => (e.id === event.id ? { ...e, status: 'finalizado' as EventStatus } : e)));
      await SwalAlert.toast('Evento finalizado.', 'success');
    }
  };

  const handleCancel = async (event: RadioEvent) => {
    const result = await SwalAlert.confirm(
      '¿Cancelar evento?',
      `"${event.title}" se marcará como cancelado. Los oyentes verán el evento como cancelado y no podrán confirmar asistencia.`,
    );
    if (result.isConfirmed) {
      setEvents((prev) => prev.map((e) => (e.id === event.id ? { ...e, status: 'cancelado' as EventStatus } : e)));
      await SwalAlert.toast('Evento cancelado.', 'success');
    }
  };

  const handleToggleHighlight = async (event: RadioEvent) => {
    const newState = !event.highlighted;
    const result = await SwalAlert.confirm(
      newState ? '¿Destacar evento?' : '¿Quitar destacado?',
      newState
        ? `"${event.title}" aparecerá primero en la sección de eventos destacados.`
        : `"${event.title}" ya no aparecerá como evento destacado.`,
    );
    if (result.isConfirmed) {
      setEvents((prev) => prev.map((e) => (e.id === event.id ? { ...e, highlighted: newState } : e)));
      await SwalAlert.toast(newState ? 'Evento destacado.' : 'Destacado removido.', 'success');
    }
  };

  const openEdit = (event: RadioEvent) => { setEditingEvent(event); setModalOpen(true); };
  const openNew = () => { setEditingEvent(null); setModalOpen(true); };
  const openDetail = (event: RadioEvent) => setDetailModal({ open: true, event });

  /* LOADING */
  if (dataState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-calendar-event-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Eventos</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
              <p className="text-foreground-500 text-sm">Cargando eventos…</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ERROR */
  if (dataState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-calendar-event-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Eventos</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center max-w-sm mx-auto px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-base mb-2">Error al cargar</h3>
              <p className="text-foreground-500 text-sm mb-5">No se pudieron cargar los eventos. Revisá tu conexión.</p>
              <button onClick={retry} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-refresh-line"></i>Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* EMPTY */
  if (dataState === 'empty') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-calendar-event-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Eventos</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center max-w-sm mx-auto px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-calendar-event-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-base mb-2">Sin eventos</h3>
              <p className="text-foreground-500 text-sm mb-5">Todavía no creaste ningún evento. ¡Organizá el primero y empezá a conectar con tu audiencia!</p>
              <button onClick={openNew} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-add-line text-base"></i>Crear primer evento
              </button>
            </div>
          </main>
        </div>
        <EventFormModal open={modalOpen} onClose={() => { setModalOpen(false); setEditingEvent(null); }} onSave={handleSave} event={editingEvent} />
      </div>
    );
  }

  /* READY */
  const kpiCards = [
    { icon: 'ri-user-fill', label: 'Asistentes', value: formatKpi(kpiTotals.participants), color: 'text-foreground-950', bgIcon: 'bg-foreground-100', iconColor: 'text-foreground-600' },
    { icon: 'ri-heart-fill', label: 'Interesados', value: formatKpi(kpiTotals.interested), color: 'text-rose-600', bgIcon: 'bg-rose-50', iconColor: 'text-rose-500' },
    { icon: 'ri-share-forward-fill', label: 'Compartidos', value: formatKpi(kpiTotals.shares), color: 'text-sky-600', bgIcon: 'bg-sky-50', iconColor: 'text-sky-500' },
    { icon: 'ri-user-follow-fill', label: 'Seguidores', value: formatKpi(kpiTotals.followers), color: 'text-emerald-600', bgIcon: 'bg-emerald-50', iconColor: 'text-emerald-500' },
  ];

  const modalitySections = [
    { modality: 'presencial' as EventModality, events: presencialEvents.filter(e => selectedStatus === 'todos' || e.status === selectedStatus) },
    { modality: 'online' as EventModality, events: onlineEvents.filter(e => selectedStatus === 'todos' || e.status === selectedStatus) },
    { modality: 'hibrido' as EventModality, events: hibridoEvents.filter(e => selectedStatus === 'todos' || e.status === selectedStatus) },
  ];

  return (
    <div className="min-h-screen bg-background-100">
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-calendar-event-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Eventos</h1>
              <p className="text-foreground-400 text-[9px]">{publishedCount} publicados</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop header */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Eventos</h2>
              <p className="text-foreground-400 text-xs">
                Gestioná eventos físicos, online e híbridos. {counts.todos} eventos en total.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-external-link-line text-xs"></i>Ver sitio
              </Link>
              <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-add-line text-base"></i>Nuevo evento
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            {/* KPI Banner */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {kpiCards.map((kpi) => (
                <div key={kpi.label} className="bg-background-50 rounded-xl border border-background-200/70 p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${kpi.bgIcon} flex items-center justify-center shrink-0`}>
                    <i className={`${kpi.icon} ${kpi.iconColor} text-base`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className={`text-lg font-bold ${kpi.color}`}>{kpi.value}</p>
                    <p className="text-foreground-400 text-[11px] font-medium">{kpi.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <EventFilters
              search={search}
              onSearchChange={setSearch}
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
              selectedModality={selectedModality}
              onModalityChange={setSelectedModality}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              counts={counts}
            />

            <div className="lg:hidden">
              <button onClick={openNew} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-add-line text-base"></i>Nuevo evento
              </button>
            </div>

            {/* Events grid */}
            {filteredEvents.length === 0 ? (
              <div className="bg-background-50 rounded-2xl border border-background-200 p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                  <i className="ri-search-line text-2xl text-foreground-400"></i>
                </div>
                <h3 className="text-foreground-950 font-bold text-base mb-1">Sin resultados</h3>
                <p className="text-foreground-500 text-sm mb-4">No hay eventos que coincidan con los filtros actuales.</p>
                <button onClick={() => { setSearch(''); setSelectedType('todos'); setSelectedStatus('todos'); setSelectedModality('todos'); setSelectedCategory('todos'); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 cursor-pointer transition-colors">
                  <i className="ri-filter-off-line text-sm"></i>Limpiar filtros
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredEvents.map((event) => (
                  <div key={event.id} onClick={() => openDetail(event)} className="cursor-pointer">
                    <EventCard
                      event={event}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onViewDetail={openDetail}
                      onShare={handleShare}
                      onPublish={handlePublish}
                      onFinalize={handleFinalize}
                      onCancelEvent={handleCancel}
                      onToggleHighlight={handleToggleHighlight}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>



      {/* Modals */}
      <EventFormModal open={modalOpen} onClose={() => { setModalOpen(false); setEditingEvent(null); }} onSave={handleSave} event={editingEvent} />
      <EventDetailModal open={detailModal.open} event={detailModal.event} onClose={() => setDetailModal({ open: false, event: null })} />
      <NotificationFlowModal open={flowModal.open} context={flowModal.context} onClose={() => setFlowModal((prev) => ({ ...prev, open: false }))} />
    </div>
  );
}